import { useMemo, useRef, useState } from 'react';
import { cn } from '@ai-tutor/ui';
import { useIsDark } from '@/hooks/ui/useIsDark';
import type { FocusSettings } from '@/lib/focus/focusAlerts';
import type { FocusView } from '@/lib/focus/focusMachine';
import { WellbeingRoom } from '../WellbeingRoom';
import { AmbientFocus } from './AmbientFocus';
import type { BreakActivity } from './BreakActivities';
import { FocusBreakPanel, FocusRunPanel, FocusSetupPanel } from './FocusPanels';
import { FocusModeLayer } from './FocusModeLayer';
import { FocusSettingsMenu } from './FocusSettingsMenu';
import { FocusStage, stageView, type FocusStageDone } from './FocusStage';
import type { PlanChoice } from './SessionPlanPanel';
import { SessionSummary, type SessionSummaryProps } from './SessionSummary';
import type {
  FocusLessonOption,
  FocusToday,
  FocusViewModel,
} from './focusViewModel';

export interface FocusSetup {
  intention: string;
  lessonId?: string;
  plan: PlanChoice;
  view: FocusView;
}

export interface FocusRoomViewProps {
  view: FocusViewModel;
  /** The set-up's choices, before Start. */
  setup: FocusSetup;
  lessons: FocusLessonOption[];
  today: FocusToday;
  /** "Now", for the plan's and the round's end times. */
  now: number;
  endsAt: number | null;
  /** When the whole session ends, while one runs. */
  sessionEndsAt: number | null;
  parkedCount: number;
  /** A just-finished session to sum up, shown in place of the set-up. */
  summary:
    | (Omit<SessionSummaryProps, 'onDismiss' | 'onKeepNotes'> & {
        rounds?: number;
      })
    | null;
  settings: FocusSettings;
  onSettingsChange: (settings: FocusSettings) => void;
  settingsOpen?: boolean;
  /** Stories open the lesson list to show it at rest. */
  lessonsOpen?: boolean;
  /** Focus mode: the timer alone, over the app. Opt-in. */
  immersive?: boolean;
  /** Absent, there is no way into focus mode (and no button for it). */
  onImmersiveChange?: (immersive: boolean) => void;
  /** Stories open the break panel on a specific activity. */
  defaultBreakActivity?: BreakActivity;
  onIntentionChange: (text: string, lessonId?: string) => void;
  onPlanChange: (patch: Partial<PlanChoice>) => void;
  onViewChange: (view: FocusView) => void;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onFinish: () => void;
  onNextBlock: () => void;
  onEndSession: () => void;
  onDismissSummary: () => void;
  /** Keep the summary's parked thoughts in the Worry dump. */
  onKeepNotes?: () => void;
  onAddNote: (text: string) => void;
  onRemoveNote: (id: string) => void;
  onSaveRecap: (text: string) => void;
  onDismissRecap: () => void;
}

const isRunning = (view: FocusViewModel) =>
  view.phase === 'focus' || view.phase === 'paused';

function Panel(props: FocusRoomViewProps & { night: boolean }) {
  const { view } = props;
  if (view.phase === 'break') {
    return (
      <FocusBreakPanel
        view={view}
        onSaveRecap={props.onSaveRecap}
        onDismissRecap={props.onDismissRecap}
        defaultActivity={props.defaultBreakActivity}
      />
    );
  }
  if (isRunning(view) || view.between) {
    return (
      <FocusRunPanel
        view={view}
        parkedCount={props.parkedCount}
        onAddNote={props.onAddNote}
        onRemoveNote={props.onRemoveNote}
      />
    );
  }
  if (props.summary) {
    return (
      <SessionSummary
        {...props.summary}
        onDismiss={props.onDismissSummary}
        onKeepNotes={props.onKeepNotes}
      />
    );
  }
  return (
    <FocusSetupPanel
      intention={props.setup.intention}
      lessonId={props.setup.lessonId}
      onIntentionChange={props.onIntentionChange}
      lessons={props.lessons}
      plan={props.setup.plan}
      onPlanChange={props.onPlanChange}
      view={props.setup.view}
      onViewChange={props.onViewChange}
      today={props.today}
      now={props.now}
      night={props.night}
      lessonsOpen={props.lessonsOpen}
    />
  );
}

/**
 * The Focus page: one card that fills the window on desktop, so nothing
 * scrolls. The stage - the classic ring, or the sakura scene in its box - is
 * the hero; the panel beside it holds only what the phase needs: set-up, the
 * parking lot, the break, the summary. Below `lg` they stack, the set-up
 * panel first so "what's this for" and the plan are chosen before Start.
 *
 * Focus mode takes the timer out of the app: the classic view as one column,
 * the sakura as the whole screen, opening out of its box.
 */
export function FocusRoomView(props: FocusRoomViewProps) {
  const { view } = props;
  const night = useIsDark();
  const settingUp = view.phase === 'idle' && !view.between && !props.summary;
  const done: FocusStageDone | null =
    view.phase === 'idle' && props.summary
      ? {
          focusMinutes: props.summary.focusMinutes,
          blocks: props.summary.blocks,
          rounds: props.summary.rounds,
        }
      : null;
  const stageProps = {
    view,
    setup: { ...props.setup.plan, view: props.setup.view },
    now: props.now,
    endsAt: props.endsAt,
    sessionEndsAt: props.sessionEndsAt,
    done,
    onStart: props.onStart,
    onPause: props.onPause,
    onResume: props.onResume,
    onFinish: props.onFinish,
    onNextBlock: props.onNextBlock,
    onEndSession: props.onEndSession,
  };
  const sakura = stageView(stageProps) === 'sakura';
  // Only a round, a break or the wait between them can be watched full screen.
  const canImmerse = !done && (view.phase !== 'idle' || view.between);
  const immersive = Boolean(props.immersive) && canImmerse;
  const box = useRef<HTMLDivElement>(null);
  const [returning, setReturning] = useState(false);
  const focusReturn = useMemo(
    () => ({ pending: returning, done: () => setReturning(false) }),
    [returning]
  );
  const exit = () => {
    setReturning(true);
    props.onImmersiveChange?.(false);
  };
  const enter =
    props.onImmersiveChange && (() => props.onImmersiveChange?.(true));
  const breakPanel = (
    <FocusBreakPanel
      view={view}
      onSaveRecap={props.onSaveRecap}
      onDismissRecap={props.onDismissRecap}
      defaultActivity={props.defaultBreakActivity}
    />
  );

  return (
    <WellbeingRoom
      fill
      title='Focus'
      description='One thing at a time, with the breaks built in.'
      dimmed={view.phase === 'focus'}
      actions={
        <FocusSettingsMenu
          settings={props.settings}
          onChange={props.onSettingsChange}
          defaultOpen={props.settingsOpen}
        />
      }
    >
      <div
        aria-hidden={immersive || undefined}
        className='grid rounded-2xl bg-card shadow-elevation-1 lg:h-full lg:grid-cols-[minmax(0,1fr)_23rem]'
      >
        <div
          className={cn(
            'flex min-h-0 flex-col p-4 sm:p-5',
            // On a phone, setting up, the stage holds only the pinned Start
            // (its face is hidden - see `FocusStage`), so it takes no room.
            settingUp && 'order-last max-sm:p-0 lg:order-none',
            !sakura && 'justify-center'
          )}
        >
          <FocusStage
            {...stageProps}
            sceneActive={!immersive}
            sceneBoxRef={box}
            onEnterFocusMode={enter}
            focusReturn={focusReturn}
          />
        </div>
        <section
          aria-label={
            props.summary
              ? 'Session summary'
              : view.phase === 'break'
                ? 'Break'
                : 'Session details'
          }
          className={cn(
            'min-h-0 overflow-y-auto border-border/60 p-5 sm:p-6 lg:border-l',
            settingUp
              ? // Room at the end for the Start button pinned over it.
                'max-sm:pb-24 sm:border-b lg:border-b-0'
              : 'border-t lg:border-t-0'
          )}
        >
          <Panel {...props} night={night} />
        </section>
      </div>
      {sakura ? (
        <FocusModeLayer
          open={immersive}
          onExit={exit}
          variant='ambient'
          fromRect={() => box.current?.getBoundingClientRect()}
        >
          {(leave: () => void) => (
            <AmbientFocus
              {...stageProps}
              onExit={leave}
              notes={view.parkingLot}
              onAddNote={props.onAddNote}
              onRemoveNote={props.onRemoveNote}
              breakPanel={breakPanel}
            />
          )}
        </FocusModeLayer>
      ) : (
        <FocusModeLayer open={immersive} onExit={exit}>
          <div className='flex w-full flex-col items-center gap-8'>
            <FocusStage {...stageProps} immersive />
            <section aria-label='Session details' className='w-full max-w-md'>
              {view.phase === 'break' ? (
                breakPanel
              ) : (
                <FocusRunPanel
                  view={view}
                  parkedCount={props.parkedCount}
                  onAddNote={props.onAddNote}
                  onRemoveNote={props.onRemoveNote}
                />
              )}
            </section>
          </div>
        </FocusModeLayer>
      )}
    </WellbeingRoom>
  );
}
